/**
 * DischargeResultsSummary Component
 * Post-submit results display — mirrors ConfirmDischargeModal layout for cohesion.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { OathButton } from '../../../shared/OathUI';
import { formatCurrencyWithSymbol } from '../utils/formatters';
import { ERROR_CODE_ACTIONS } from '../utils/constants';

const COLLAPSE_THRESHOLD = 6;

// ─── Power row (matches ConfirmDischargeModal.PowerRow) ─────────────────────

const PowerRow = ({ powerNo, defendant, amount }) => (
  <tr className="border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
    <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2">
      <span className="font-mono font-semibold text-xs qhd:text-sm text-primary dark:text-primary-light">
        {powerNo}
      </span>
    </td>
    <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2 text-xs qhd:text-sm text-gray-700 dark:text-gray-300 truncate max-w-[10rem]">
      {defendant || '\u2014'}
    </td>
    <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2 text-right text-xs qhd:text-sm font-medium text-gray-900 dark:text-white tabular-nums">
      {amount != null ? formatCurrencyWithSymbol(amount) : '\u2014'}
    </td>
  </tr>
);

const ErrorRow = ({ powerNo, message, errorCode }) => (
  <tr className="border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
    <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2">
      <span className="font-mono font-semibold text-xs qhd:text-sm text-primary dark:text-primary-light">
        {powerNo}
      </span>
    </td>
    <td colSpan={2} className="px-2.5 py-1.5 qhd:px-3 qhd:py-2 text-xs qhd:text-sm text-red-600 dark:text-red-400">
      {ERROR_CODE_ACTIONS[errorCode] || message}
    </td>
  </tr>
);

// ─── Collapsible list (matches ConfirmDischargeModal.CollapsiblePowerList) ──

const CollapsiblePowerList = ({ title, count, color, borderClass, notice, children }) => {
  const shouldCollapse = count > COLLAPSE_THRESHOLD;
  const [expanded, setExpanded] = useState(!shouldCollapse);

  const dotClass = color === 'green' ? 'bg-green-500' : color === 'orange' ? 'bg-orange-500' : 'bg-red-500';
  const border = borderClass || 'border-gray-200 dark:border-gray-700';

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-1.5 py-1.5 sm:py-0 text-xs qhd:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1.5 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
      >
        <span className={`h-2 w-2 rounded-full ${dotClass}`} />
        {title} ({count})
        <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className={`rounded-lg border ${border} overflow-hidden max-h-48 overflow-y-auto`}>
          {notice && (
            <div className="px-2.5 py-1.5 qhd:px-3 qhd:py-2 bg-orange-50/60 dark:bg-orange-900/10 border-b border-orange-100 dark:border-orange-800/30">
              <p className="text-[11px] qhd:text-xs text-orange-700 dark:text-orange-400 leading-relaxed flex items-center gap-1.5">
                <ClockIcon className="h-3 w-3 flex-shrink-0" />
                {notice}
              </p>
            </div>
          )}
          <table className="min-w-full">
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const DischargeResultsSummary = ({ result, validationData, onDischargeMore, onViewHistory }) => {
  const validationMap = useMemo(() => {
    if (!validationData || !Array.isArray(validationData)) return {};
    return validationData.reduce((map, item) => {
      if (item.powerNo) map[item.powerNo] = item;
      return map;
    }, {});
  }, [validationData]);

  const lookupPower = useCallback(
    (powerNo) => {
      const v = validationMap[powerNo];
      return {
        defendant: v?.defendantName ?? v?.defendant ?? null,
        amount: v?.bondAmount ?? v?.liability ?? null,
      };
    },
    [validationMap],
  );

  const {
    success,
    reportNumber,
    processedCount = 0,
    pendingReviewCount = 0,
    totalLiability = 0,
    dischargedPowers = [],
    pendingPowers = [],
    errors: rawErrors = [],
    message,
  } = result || {};

  const pendingSet = useMemo(() => new Set(pendingPowers), [pendingPowers]);
  const errors = useMemo(
    () => rawErrors.filter((err) => !(err.errorCode === 'PROOF_REQUIRED' && pendingSet.has(err.powerNo))),
    [rawErrors, pendingSet],
  );

  if (!result) return null;

  const totalCount = processedCount + pendingReviewCount;

  return (
    <div className="space-y-3">
      {/* ── Summary bar (mirrors ConfirmDischargeModal) ──────────────── */}
      <div className={`rounded-xl px-4 py-3 qhd:px-5 qhd:py-4 flex flex-wrap items-center justify-between gap-3 ${
        success
          ? 'bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800/50'
          : 'bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/50'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg ${success ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
            {success
              ? <CheckCircleIcon className="h-5 w-5 qhd:h-6 qhd:w-6 text-green-600 dark:text-green-400" />
              : <ExclamationCircleIcon className="h-5 w-5 qhd:h-6 qhd:w-6 text-red-600 dark:text-red-400" />
            }
          </div>
          <div>
            <p className="text-xs qhd:text-sm text-gray-500 dark:text-gray-400">
              {reportNumber && (
                <>Report <span className="font-mono font-semibold text-primary dark:text-primary-light">{reportNumber}</span></>
              )}
            </p>
            <p className="text-base qhd:text-lg font-bold text-gray-900 dark:text-white">
              {message || (success ? 'Discharge submitted' : 'Discharge failed')}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs qhd:text-sm">
          {processedCount > 0 && (
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <strong>{processedCount}</strong> discharged
            </span>
          )}
          {pendingReviewCount > 0 && (
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-orange-500" />
              <strong>{pendingReviewCount}</strong> pending
            </span>
          )}
          {errors.length > 0 && (
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <strong>{errors.length}</strong> error{errors.length !== 1 ? 's' : ''}
            </span>
          )}
          {totalLiability > 0 && (
            <span className="font-bold text-gray-800 dark:text-gray-200 tabular-nums">
              {formatCurrencyWithSymbol(totalLiability)}
            </span>
          )}
        </div>
      </div>

      {/* ── Power lists (same grid + CollapsiblePowerList as confirm modal) */}
      {(dischargedPowers.length > 0 || pendingPowers.length > 0 || errors.length > 0) && (
        <div className={`grid gap-3 ${
          dischargedPowers.length > 0 && pendingPowers.length > 0 && errors.length === 0
            ? 'grid-cols-1 sm:grid-cols-2'
            : 'grid-cols-1'
        }`}>
          {dischargedPowers.length > 0 && (
            <CollapsiblePowerList
              title="Discharged"
              count={dischargedPowers.length}
              color="green"
            >
              {dischargedPowers.map((powerNo) => {
                const { defendant, amount } = lookupPower(powerNo);
                return <PowerRow key={powerNo} powerNo={powerNo} defendant={defendant} amount={amount} />;
              })}
            </CollapsiblePowerList>
          )}

          {pendingPowers.length > 0 && (
            <CollapsiblePowerList
              title="Pending Review"
              count={pendingPowers.length}
              color="orange"
              borderClass="border-orange-200 dark:border-orange-800/50"
              notice="Will be reviewed and processed within 2 business days."
            >
              {pendingPowers.map((powerNo) => {
                const { defendant, amount } = lookupPower(powerNo);
                return <PowerRow key={powerNo} powerNo={powerNo} defendant={defendant} amount={amount} />;
              })}
            </CollapsiblePowerList>
          )}

          {errors.length > 0 && (
            <CollapsiblePowerList
              title="Errors"
              count={errors.length}
              color="red"
              borderClass="border-red-200 dark:border-red-800/50"
            >
              {errors.map((err, idx) => (
                <ErrorRow key={err.powerNo || idx} powerNo={err.powerNo} message={err.message} errorCode={err.errorCode} />
              ))}
            </CollapsiblePowerList>
          )}
        </div>
      )}

      {/* ── Actions ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-center gap-3 pt-1">
        <OathButton onClick={onDischargeMore} size="sm">
          <ArrowPathIcon className="h-4 w-4 mr-1.5" />
          Discharge More
        </OathButton>
        <OathButton variant="outline" onClick={onViewHistory} size="sm">
          <ClipboardDocumentListIcon className="h-4 w-4 mr-1.5" />
          View History
        </OathButton>
      </div>
    </div>
  );
};

export default DischargeResultsSummary;
